import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { Loader2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTRPC } from "@/trpc";
import { cn } from "@/lib/utils";

const NO_ORG_MESSAGE =
  "You need an organization to add providers. Create one to get started.";
const NOT_SHAREABLE_MESSAGE =
  "This provider hasn't allowed their profile to be shared with others.";

// Disabled-*looking* button that still receives pointer events — a real
// `disabled` attribute would kill hover/click, and this state needs both
// (tooltip on hover, a message or redirect on click/tap for mobile).
const disabledLookClassName =
  "cursor-not-allowed border-slate-200 text-slate-400 hover:bg-transparent hover:text-slate-400";

type SharedProvider = {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  directShareAllowsResharing: boolean | null;
};

export function AddToOrganizationButton({
  provider,
  organizationBusinessUnitId,
  canManageAffiliations,
  className,
}: {
  provider: SharedProvider;
  organizationBusinessUnitId: string | undefined;
  canManageAffiliations: boolean;
  className?: string;
}) {
  const trpc = useTRPC();
  const navigate = useNavigate();

  const addProviderMutate = useMutation(
    trpc.organizationBusinessUnitAffiliation.addProvider.mutationOptions({
      onSuccess: (result) => {
        const name =
          `${provider.firstName ?? ""} ${provider.lastName ?? ""}`.trim() ||
          "Provider";
        toast.success(
          result.alreadyAffiliated
            ? `${name} is already in your organization.`
            : `${name} added to your organization as a candidate.`,
        );
      },
      onError: (error) => {
        toast.error(error.message || "Failed to add provider.");
      },
    }),
  );

  // Viewer has an org but can't manage affiliations — the action isn't
  // available to them at all, so don't show it.
  if (organizationBusinessUnitId && !canManageAffiliations) {
    return null;
  }

  if (!organizationBusinessUnitId) {
    return (
      <TooltipProvider>
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={cn(
                "flex items-center gap-1",
                disabledLookClassName,
                className,
              )}
              onClick={() => navigate({ to: "/org/create" })}
            >
              <UserPlus className="h-3.5 w-3.5" />
              <span>Add to Organization</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>{NO_ORG_MESSAGE}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (provider.directShareAllowsResharing !== true) {
    return (
      <TooltipProvider>
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={cn(
                "flex items-center gap-1",
                disabledLookClassName,
                className,
              )}
              onClick={() => toast.info(NOT_SHAREABLE_MESSAGE)}
            >
              <UserPlus className="h-3.5 w-3.5" />
              <span>Add to Organization</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>{NOT_SHAREABLE_MESSAGE}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={cn("flex items-center gap-1", className)}
      disabled={addProviderMutate.isPending}
      onClick={() =>
        addProviderMutate.mutate({
          organizationBusinessUnitId,
          userId: provider.userId,
        })
      }
    >
      {addProviderMutate.isPending ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <UserPlus className="h-3.5 w-3.5" />
      )}
      <span>Add to Organization</span>
    </Button>
  );
}
